"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowDownOutline18, IconBoltOutline18, IconCodeBranchOutline18, IconPlusOutline18 } from "nucleo-ui-outline-18";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { NodeExecutionStatus } from "@/types/runStream";
import { buildWorkflowGraphLookups, collectLoopBodyDescendantsForForEach, getDirectLoopBodyChildren } from "../_lib/workflowGraphAnalysis";
import { EdgeLabelRenderer, useEdges, useNodes } from "@xyflow/react";
import { memo, useCallback, useMemo, useState } from "react";
interface ForEachLoopBoundaryProps {
  onAddOnCompleteAction?: (forEachNodeId: string) => void;
  onAddOnCompleteLogic?: (forEachNodeId: string) => void;
  onAddLoopBodyAction?: (forEachNodeId: string) => void;
  onAddLoopBodyLogic?: (forEachNodeId: string) => void;
  onAddActionBetween?: (sourceId: string, targetId: string) => void;
  onAddLogicBetween?: (sourceId: string, targetId: string) => void;
  disabled?: boolean;
}
interface IncomingConnection {
  sourceNodeId: string;
  startX: number;
  startY: number;
  sourceHandle: string | null;
  sourceStatus?: NodeExecutionStatus;
  targetStatus?: NodeExecutionStatus;
  hasActiveRun?: boolean;
}
interface OnCompleteConnection {
  targetNodeId: string;
  targetCenterX: number;
  targetY: number;
  sourceStatus?: NodeExecutionStatus;
  targetStatus?: NodeExecutionStatus;
  hasActiveRun?: boolean;
}
interface BoundaryOverlay {
  forEachNodeId: string;
  forEachCenterX: number;
  forEachBottomY: number;
  x: number;
  y: number;
  width: number;
  height: number;
  hasLoopBodyChildren: boolean;
  incomingConnections: IncomingConnection[];
  onCompleteConnections: OnCompleteConnection[];
}
const PADDING = 20;
const PADDING_TOP = 20; // Same as PADDING — label sits on the border edge, not inside
const PADDING_BOTTOM_EMPTY_LOOP = 60; // Extra bottom padding when loop body is empty (fits "+" button)
const LABEL_HEIGHT = 20; // Height of the "On Complete" label badge
const STROKE_DASH_INCOMING = "8 4";
const BORDER_RADIUS = 12;
const PLUS_BTN_OFFSET = 12; // Gap below boundary label to "+" button center

function getBoundaryEdgeVisual({
  sourceStatus,
  targetStatus,
  hasActiveRun,
  isBranchEdge = false
}: {
  sourceStatus?: NodeExecutionStatus;
  targetStatus?: NodeExecutionStatus;
  hasActiveRun?: boolean;
  isBranchEdge?: boolean;
}) {
  if (!hasActiveRun) {
    return {
      stroke: "#3b82f6",
      strokeDasharray: STROKE_DASH_INCOMING,
      className: "edge-animated"
    };
  }
  if (isBranchEdge && sourceStatus === "success" && (targetStatus === "idle" || targetStatus === "skipped")) {
    return {
      stroke: "#a1a1aa",
      strokeDasharray: "6 4",
      className: ""
    };
  }
  switch (sourceStatus) {
    case "running":
      return {
        stroke: "#f97316",
        strokeDasharray: "6 4",
        className: "edge-animated edge-animated--fast"
      };
    case "waiting":
      return {
        stroke: "#f59e0b",
        strokeDasharray: "6 4",
        className: "edge-animated"
      };
    case "success":
      return {
        stroke: "#22c55e",
        strokeDasharray: "6 4",
        className: "edge-animated edge-animated--medium"
      };
    case "failure":
      return {
        stroke: "#ef4444",
        strokeDasharray: "6 4",
        className: "edge-animated edge-animated--medium"
      };
    case "idle":
    case "skipped":
    default:
      return {
        stroke: "#a1a1aa",
        strokeDasharray: "6 4",
        className: ""
      };
  }
}
function ForEachLoopBoundaryInner(t0) {
  const $ = _c(14);
  const {
    onAddOnCompleteAction,
    onAddOnCompleteLogic,
    onAddLoopBodyAction,
    onAddLoopBodyLogic,
    onAddActionBetween,
    onAddLogicBetween,
    disabled
  } = t0;
  const nodes = useNodes();
  const edges = useEdges();
  let t1;
  bb0: {
    let result;
    if ($[0] !== disabled || $[1] !== edges || $[2] !== nodes) {
      result = [];
      if (nodes.some(_temp)) {
        t1 = result;
        break bb0;
      }
      const {
        childrenMap,
        nodeMap,
        onCompleteTargetIds,
        forEachNodes
      } = buildWorkflowGraphLookups(nodes, edges);
      if (forEachNodes.length === 0) {
        t1 = result;
        break bb0;
      }
      for (const feNode of forEachNodes) {
        const directChildIds = getDirectLoopBodyChildren(feNode.id, childrenMap, onCompleteTargetIds);
        const loopBodyIds = collectLoopBodyDescendantsForForEach(feNode.id, childrenMap, onCompleteTargetIds);
        const allIds = [feNode.id, ...loopBodyIds];
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const id of allIds) {
          if (id.startsWith("placeholder-")) {
            continue;
          }
          const node = nodeMap.get(id);
          if (!node) {
            continue;
          }
          const w = node.measured?.width || 280;
          const h = node.measured?.height || 80;
          minX = Math.min(minX, node.position.x);
          minY = Math.min(minY, node.position.y);
          maxX = Math.max(maxX, node.position.x + w);
          maxY = Math.max(maxY, node.position.y + h);
        }
        if (minX === Infinity) {
          continue;
        }
        const incomingConnections = [];
        for (const e of edges) {
          if (e.target === feNode.id && !loopBodyIds.has(e.source) && e.source !== feNode.id) {
            const sourceNode = nodeMap.get(e.source);
            if (sourceNode) {
              const sw = sourceNode.measured?.width || 280;
              const sh = sourceNode.measured?.height || 80;
              const handle = e.sourceHandle || null;
              let startX = sourceNode.position.x + sw / 2;
              let startY = sourceNode.position.y + sh;
              if (handle === "source-then" || handle === "source-resumed") {
                startX = sourceNode.position.x + sw;
                startY = sourceNode.position.y + sh / 2;
              } else {
                if (handle === "source-else" || handle === "source-timed_out") {
                  startX = sourceNode.position.x;
                  startY = sourceNode.position.y + sh / 2;
                }
              }
              incomingConnections.push({
                sourceNodeId: sourceNode.id,
                startX,
                startY,
                sourceHandle: handle,
                sourceStatus: e.data?.sourceNodeStatus as NodeExecutionStatus | undefined,
                targetStatus: e.data?.targetNodeStatus as NodeExecutionStatus | undefined,
                hasActiveRun: e.data?.hasActiveRun as boolean | undefined
              });
            }
          }
        }
        const onCompleteConnections = [];
        for (const e_0 of edges) {
          if (e_0.source === feNode.id && onCompleteTargetIds.has(e_0.target)) {
            const targetNode = nodeMap.get(e_0.target);
            if (targetNode) {
              const tw = targetNode.measured?.width || 280;
              onCompleteConnections.push({
                targetNodeId: targetNode.id,
                targetCenterX: targetNode.position.x + tw / 2,
                targetY: targetNode.position.y,
                sourceStatus: e_0.data?.sourceNodeStatus as NodeExecutionStatus | undefined,
                targetStatus: e_0.data?.targetNodeStatus as NodeExecutionStatus | undefined,
                hasActiveRun: e_0.data?.hasActiveRun as boolean | undefined
              });
            }
          }
        }
        const feW = feNode.measured?.width || 280;
        const feH = feNode.measured?.height || 80;
        result.push({
          forEachNodeId: feNode.id,
          forEachCenterX: feNode.position.x + feW / 2,
          forEachBottomY: feNode.position.y + feH,
          x: minX - PADDING,
          y: minY - PADDING_TOP,
          width: maxX - minX + PADDING * 2,
          height: maxY - minY + PADDING_TOP + (directChildIds.length === 0 && !disabled ? PADDING_BOTTOM_EMPTY_LOOP : PADDING),
          hasLoopBodyChildren: directChildIds.length > 0,
          incomingConnections,
          onCompleteConnections
        });
      }
      $[0] = disabled;
      $[1] = edges;
      $[2] = nodes;
      $[3] = result;
      $[4] = t1;
    } else {
      result = $[3];
      t1 = $[4];
    }
    t1 = result;
  }
  const overlays = t1;
  if (overlays.length === 0) {
    return null;
  }
  let t2;
  if ($[5] !== disabled || $[6] !== onAddActionBetween || $[7] !== onAddLogicBetween || $[8] !== onAddLoopBodyAction || $[9] !== onAddLoopBodyLogic || $[10] !== onAddOnCompleteAction || $[11] !== onAddOnCompleteLogic || $[12] !== overlays) {
    t2 = <EdgeLabelRenderer>{overlays.map(overlay => <ForEachBoundaryBox key={overlay.forEachNodeId} overlay={overlay} disabled={disabled} onAddOnCompleteAction={onAddOnCompleteAction} onAddOnCompleteLogic={onAddOnCompleteLogic} onAddLoopBodyAction={onAddLoopBodyAction} onAddLoopBodyLogic={onAddLoopBodyLogic} onAddActionBetween={onAddActionBetween} onAddLogicBetween={onAddLogicBetween} />)}</EdgeLabelRenderer>;
    $[5] = disabled;
    $[6] = onAddActionBetween;
    $[7] = onAddLogicBetween;
    $[8] = onAddLoopBodyAction;
    $[9] = onAddLoopBodyLogic;
    $[10] = onAddOnCompleteAction;
    $[11] = onAddOnCompleteLogic;
    $[12] = overlays;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}
function _temp(n) {
  return (n.data as Record<string, unknown>)?.isStreaming;
}
interface IncomingConnectionLineProps {
  conn: IncomingConnection;
  forEachNodeId: string;
  endX: number;
  endY: number;
  disabled?: boolean;
  onAddActionBetween?: (sourceId: string, targetId: string) => void;
  onAddLogicBetween?: (sourceId: string, targetId: string) => void;
}
function IncomingConnectionLine(t0) {
  const $ = _c(29);
  const {
    conn,
    forEachNodeId,
    endX,
    endY,
    disabled,
    onAddActionBetween,
    onAddLogicBetween
  } = t0;
  const [menuOpen, setMenuOpen] = useState(false);
  const startX = conn.startX;
  const startY = conn.startY;
  let path;
  const isBranchHandle = conn.sourceHandle === "source-then" || conn.sourceHandle === "source-else" || conn.sourceHandle === "source-resumed" || conn.sourceHandle === "source-timed_out";
  if (isBranchHandle) {
    path = Math.abs(startX - endX) < 2 ? `M ${startX} ${startY} L ${endX} ${endY}` : `M ${startX} ${startY} L ${endX} ${startY} L ${endX} ${endY}`;
  } else {
    const midY = (startY + endY) / 2;
    path = Math.abs(startX - endX) < 2 ? `M ${startX} ${startY} L ${endX} ${endY}` : `M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`;
  }
  const btnX = (startX + endX) / 2;
  const btnY = isBranchHandle ? startY : (startY + endY) / 2;
  let t1;
  if ($[0] !== conn.hasActiveRun || $[1] !== conn.sourceStatus || $[2] !== conn.targetStatus || $[3] !== isBranchHandle) {
    t1 = getBoundaryEdgeVisual({
      sourceStatus: conn.sourceStatus,
      targetStatus: conn.targetStatus,
      hasActiveRun: conn.hasActiveRun,
      isBranchEdge: isBranchHandle
    });
    $[0] = conn.hasActiveRun;
    $[1] = conn.sourceStatus;
    $[2] = conn.targetStatus;
    $[3] = isBranchHandle;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const edgeVisual = t1;
  let t2;
  if ($[5] !== conn.sourceNodeId || $[6] !== forEachNodeId || $[7] !== onAddActionBetween) {
    t2 = () => {
      setMenuOpen(false);
      onAddActionBetween?.(conn.sourceNodeId, forEachNodeId);
    };
    $[5] = conn.sourceNodeId;
    $[6] = forEachNodeId;
    $[7] = onAddActionBetween;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleAddAction = t2;
  let t3;
  if ($[9] !== conn.sourceNodeId || $[10] !== forEachNodeId || $[11] !== onAddLogicBetween) {
    t3 = () => {
      setMenuOpen(false);
      onAddLogicBetween?.(conn.sourceNodeId, forEachNodeId);
    };
    $[9] = conn.sourceNodeId;
    $[10] = forEachNodeId;
    $[11] = onAddLogicBetween;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleAddLogic = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      overflow: "visible",
      width: 1,
      height: 1
    };
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== edgeVisual.className || $[15] !== edgeVisual.stroke || $[16] !== edgeVisual.strokeDasharray || $[17] !== path) {
    t5 = <svg className="foreach-loop-boundary pointer-events-none absolute" style={t4}><path d={path} stroke={edgeVisual.stroke} strokeWidth={2} strokeDasharray={edgeVisual.strokeDasharray} fill="none" className={edgeVisual.className} /></svg>;
    $[14] = edgeVisual.className;
    $[15] = edgeVisual.stroke;
    $[16] = edgeVisual.strokeDasharray;
    $[17] = path;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== btnX || $[20] !== btnY || $[21] !== disabled || $[22] !== handleAddAction || $[23] !== handleAddLogic || $[24] !== menuOpen) {
    t6 = !disabled && <div className="foreach-loop-boundary nodrag nopan absolute" style={{
      transform: `translate(-50%, -50%) translate(${btnX}px, ${btnY}px)`,
      pointerEvents: "all"
    }}><Popover open={menuOpen} onOpenChange={open => {
        if (!open) {
          setMenuOpen(false);
        }
      }} modal={false}><PopoverTrigger asChild={true}><button className="border-ds-neutral-300 text-ds-text-subtle dark:text-ds-text-disabled bg-ds-surface-card shadow-ds-soft flex h-6 w-6 items-center justify-center rounded-full border transition-all hover:h-7 hover:w-7 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 hover:shadow-md dark:border-zinc-600 dark:hover:border-blue-500 dark:hover:bg-blue-950 dark:hover:text-blue-400" onClick={e => {
            e.stopPropagation();
            setMenuOpen(_temp2);
          }}><IconPlusOutline18 className="h-3 w-3" /></button></PopoverTrigger><PopoverContent className="border-ds-border-default bg-ds-surface-card w-64 p-2 shadow-xl" align="center" onPointerDownOutside={() => setMenuOpen(false)}><div className="flex flex-col"><button onClick={handleAddAction} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-green-50 dark:hover:bg-green-950/20"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-50 dark:bg-green-950/30"><IconBoltOutline18 className="h-4.5 w-4.5 text-green-600 dark:text-green-400" /></div><div className="flex-1"><div className="body-sm-semibold">Add Action</div><div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">Execute a workflow action</div></div></button><div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-1 h-px" /><button onClick={handleAddLogic} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-purple-50 dark:hover:bg-purple-950/20"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-950/30"><IconCodeBranchOutline18 className="h-4.5 w-4.5 text-purple-600 dark:text-purple-400" /></div><div className="flex-1"><div className="body-sm-semibold">Add Logic Gate</div><div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">Control flow conditionally</div></div></button></div></PopoverContent></Popover></div>;
    $[19] = btnX;
    $[20] = btnY;
    $[21] = disabled;
    $[22] = handleAddAction;
    $[23] = handleAddLogic;
    $[24] = menuOpen;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t5 || $[27] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
}
function _temp2(prev) {
  return !prev;
}
interface ForEachBoundaryBoxProps {
  overlay: BoundaryOverlay;
  disabled?: boolean;
  onAddOnCompleteAction?: (forEachNodeId: string) => void;
  onAddOnCompleteLogic?: (forEachNodeId: string) => void;
  onAddLoopBodyAction?: (forEachNodeId: string) => void;
  onAddLoopBodyLogic?: (forEachNodeId: string) => void;
  onAddActionBetween?: (sourceId: string, targetId: string) => void;
  onAddLogicBetween?: (sourceId: string, targetId: string) => void;
}
function ForEachBoundaryBox(t0) {
  const $ = _c(71);
  const {
    overlay,
    disabled,
    onAddOnCompleteAction,
    onAddOnCompleteLogic,
    onAddLoopBodyAction,
    onAddLoopBodyLogic,
    onAddActionBetween,
    onAddLogicBetween
  } = t0;
  const [menuOpen, setMenuOpen] = useState(false);
  const [loopBodyMenuOpen, setLoopBodyMenuOpen] = useState(false);
  let t1;
  if ($[0] !== onAddOnCompleteAction || $[1] !== overlay.forEachNodeId) {
    t1 = () => {
      onAddOnCompleteAction?.(overlay.forEachNodeId);
      setMenuOpen(false);
    };
    $[0] = onAddOnCompleteAction;
    $[1] = overlay.forEachNodeId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleAddAction = t1;
  let t2;
  if ($[3] !== onAddOnCompleteLogic || $[4] !== overlay.forEachNodeId) {
    t2 = () => {
      onAddOnCompleteLogic?.(overlay.forEachNodeId);
      setMenuOpen(false);
    };
    $[3] = onAddOnCompleteLogic;
    $[4] = overlay.forEachNodeId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleAddLogic = t2;
  let t3;
  if ($[6] !== onAddLoopBodyAction || $[7] !== overlay.forEachNodeId) {
    t3 = () => {
      onAddLoopBodyAction?.(overlay.forEachNodeId);
      setLoopBodyMenuOpen(false);
    };
    $[6] = onAddLoopBodyAction;
    $[7] = overlay.forEachNodeId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleAddLoopBodyAction = t3;
  let t4;
  if ($[9] !== onAddLoopBodyLogic || $[10] !== overlay.forEachNodeId) {
    t4 = () => {
      onAddLoopBodyLogic?.(overlay.forEachNodeId);
      setLoopBodyMenuOpen(false);
    };
    $[9] = onAddLoopBodyLogic;
    $[10] = overlay.forEachNodeId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleAddLoopBodyLogic = t4;
  const centerX = overlay.x + overlay.width / 2;
  const boundaryBottom = overlay.y + overlay.height;
  const plusBtnY = overlay.onCompleteConnections.length > 0 ? (boundaryBottom + LABEL_HEIGHT + Math.min(...overlay.onCompleteConnections.map(_temp3))) / 2 : boundaryBottom + LABEL_HEIGHT + PLUS_BTN_OFFSET + 12;
  let t5;
  if ($[12] !== disabled || $[13] !== onAddActionBetween || $[14] !== onAddLogicBetween || $[15] !== overlay.forEachCenterX || $[16] !== overlay.forEachNodeId || $[17] !== overlay.incomingConnections || $[18] !== overlay.y) {
    let t6;
    if ($[20] !== disabled || $[21] !== onAddActionBetween || $[22] !== onAddLogicBetween || $[23] !== overlay.forEachCenterX || $[24] !== overlay.forEachNodeId || $[25] !== overlay.y) {
      t6 = conn => <IncomingConnectionLine key={`incoming-${conn.sourceNodeId}`} conn={conn} forEachNodeId={overlay.forEachNodeId} endX={overlay.forEachCenterX} endY={overlay.y} disabled={disabled} onAddActionBetween={onAddActionBetween} onAddLogicBetween={onAddLogicBetween} />;
      $[20] = disabled;
      $[21] = onAddActionBetween;
      $[22] = onAddLogicBetween;
      $[23] = overlay.forEachCenterX;
      $[24] = overlay.forEachNodeId;
      $[25] = overlay.y;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    t5 = overlay.incomingConnections.map(t6);
    $[12] = disabled;
    $[13] = onAddActionBetween;
    $[14] = onAddLogicBetween;
    $[15] = overlay.forEachCenterX;
    $[16] = overlay.forEachNodeId;
    $[17] = overlay.incomingConnections;
    $[18] = overlay.y;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = `translate(${overlay.x}px, ${overlay.y}px)`;
  let t7;
  if ($[27] !== overlay.height || $[28] !== overlay.width || $[29] !== t6) {
    t7 = {
      transform: t6,
      width: overlay.width,
      height: overlay.height,
      border: "2px dashed",
      borderColor: "rgba(217, 119, 6, 0.4)",
      borderRadius: BORDER_RADIUS
    };
    $[27] = overlay.height;
    $[28] = overlay.width;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="micro-label absolute -top-3 left-3 rounded-full bg-amber-100 px-2.5 py-0 text-amber-700 backdrop-blur-sm dark:bg-amber-900/50 dark:text-amber-300" style={{
      pointerEvents: "none"
    }}>Loop</span>;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t10;
  let t9;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      pointerEvents: "none"
    };
    t10 = <IconArrowDownOutline18 className="h-2.5 w-2.5" />;
    $[32] = t10;
    $[33] = t9;
  } else {
    t10 = $[32];
    t9 = $[33];
  }
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="micro-label absolute -bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-2.5 py-0 text-amber-700 backdrop-blur-md dark:bg-amber-900/50 dark:text-amber-300" style={t9}>{t10} On Complete{" "}<IconArrowDownOutline18 className="h-2.5 w-2.5" /></span>;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t7) {
    t12 = <div className="foreach-loop-boundary pointer-events-none absolute" style={t7}>{t8}{t11}</div>;
    $[35] = t7;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== disabled || $[38] !== handleAddLoopBodyAction || $[39] !== handleAddLoopBodyLogic || $[40] !== loopBodyMenuOpen || $[41] !== overlay.forEachBottomY || $[42] !== overlay.forEachCenterX || $[43] !== overlay.hasLoopBodyChildren) {
    t13 = !disabled && !overlay.hasLoopBodyChildren && <><svg className="foreach-loop-boundary pointer-events-none absolute" style={{
        overflow: "visible",
        width: 1,
        height: 1
      }}><path d={`M ${overlay.forEachCenterX} ${overlay.forEachBottomY} L ${overlay.forEachCenterX} ${overlay.forEachBottomY + 40}`} stroke="#3b82f6" strokeWidth={2} strokeDasharray={STROKE_DASH_INCOMING} fill="none" className="edge-animated" /></svg><div className="foreach-loop-boundary nodrag nopan absolute" style={{
        transform: `translate(-50%, -50%) translate(${overlay.forEachCenterX}px, ${overlay.forEachBottomY + 40}px)`,
        pointerEvents: "all"
      }}><Popover open={loopBodyMenuOpen} onOpenChange={open => {
          if (!open) {
            setLoopBodyMenuOpen(false);
          }
        }} modal={false}><PopoverTrigger asChild={true}><button className="border-ds-neutral-300 text-ds-text-subtle dark:text-ds-text-disabled bg-ds-surface-card shadow-ds-soft flex h-6 w-6 items-center justify-center rounded-full border transition-all hover:h-7 hover:w-7 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 hover:shadow-md dark:border-zinc-600 dark:hover:border-blue-500 dark:hover:bg-blue-950 dark:hover:text-blue-400" onClick={e => {
              e.stopPropagation();
              setLoopBodyMenuOpen(_temp4);
            }} title="Add node inside loop body (runs on each iteration)"><IconPlusOutline18 className="h-3 w-3" /></button></PopoverTrigger><PopoverContent className="border-ds-border-default bg-ds-surface-card w-64 p-2 shadow-xl" align="center" onPointerDownOutside={() => setLoopBodyMenuOpen(false)}><div className="micro-label mb-1.5 px-3 pt-1 font-semibold text-amber-600 dark:text-amber-400">Loop Body (per iteration)</div><div className="flex flex-col"><button onClick={handleAddLoopBodyAction} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-green-50 dark:hover:bg-green-950/20"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-50 dark:bg-green-950/30"><IconBoltOutline18 className="h-4.5 w-4.5 text-green-600 dark:text-green-400" /></div><div className="flex-1"><div className="body-sm-semibold">Add Action</div><div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">Runs on each iteration</div></div></button><div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-1 h-px" /><button onClick={handleAddLoopBodyLogic} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-purple-50 dark:hover:bg-purple-950/20"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-950/30"><IconCodeBranchOutline18 className="h-4.5 w-4.5 text-purple-600 dark:text-purple-400" /></div><div className="flex-1"><div className="body-sm-semibold">Add Logic Gate</div><div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">Control flow conditionally</div></div></button></div></PopoverContent></Popover></div></>;
    $[37] = disabled;
    $[38] = handleAddLoopBodyAction;
    $[39] = handleAddLoopBodyLogic;
    $[40] = loopBodyMenuOpen;
    $[41] = overlay.forEachBottomY;
    $[42] = overlay.forEachCenterX;
    $[43] = overlay.hasLoopBodyChildren;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== boundaryBottom || $[46] !== centerX || $[47] !== overlay.onCompleteConnections) {
    let t15;
    if ($[49] !== boundaryBottom || $[50] !== centerX) {
      t15 = conn_0 => {
        const endX = conn_0.targetCenterX;
        const endY = conn_0.targetY;
        const midY = (boundaryBottom + LABEL_HEIGHT + endY) / 2;
        const edgeVisual = getBoundaryEdgeVisual({
          sourceStatus: conn_0.sourceStatus,
          targetStatus: conn_0.targetStatus,
          hasActiveRun: conn_0.hasActiveRun
        });
        const path = Math.abs(centerX - endX) < 2 ? `M ${centerX} ${boundaryBottom} L ${endX} ${endY}` : `M ${centerX} ${boundaryBottom} L ${centerX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`;
        return <svg key={conn_0.targetNodeId} className="foreach-loop-boundary pointer-events-none absolute" style={{
          overflow: "visible",
          width: 1,
          height: 1
        }}><path d={path} stroke={edgeVisual.stroke} strokeWidth={2} strokeDasharray={edgeVisual.strokeDasharray} fill="none" className={edgeVisual.className} /></svg>;
      };
      $[49] = boundaryBottom;
      $[50] = centerX;
      $[51] = t15;
    } else {
      t15 = $[51];
    }
    t14 = overlay.onCompleteConnections.map(t15);
    $[45] = boundaryBottom;
    $[46] = centerX;
    $[47] = overlay.onCompleteConnections;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[52] !== boundaryBottom || $[53] !== centerX || $[54] !== overlay.onCompleteConnections.length || $[55] !== plusBtnY) {
    t15 = overlay.onCompleteConnections.length === 0 && <svg className="foreach-loop-boundary pointer-events-none absolute" style={{
      overflow: "visible",
      width: 1,
      height: 1
    }}><path d={`M ${centerX} ${boundaryBottom} L ${centerX} ${plusBtnY}`} stroke="#3b82f6" strokeWidth={2} strokeDasharray={STROKE_DASH_INCOMING} fill="none" className="edge-animated" /></svg>;
    $[52] = boundaryBottom;
    $[53] = centerX;
    $[54] = overlay.onCompleteConnections.length;
    $[55] = plusBtnY;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== centerX || $[58] !== disabled || $[59] !== handleAddAction || $[60] !== handleAddLogic || $[61] !== menuOpen || $[62] !== plusBtnY) {
    t16 = !disabled && <div className="foreach-loop-boundary nodrag nopan absolute" style={{
      transform: `translate(-50%, -50%) translate(${centerX}px, ${plusBtnY}px)`,
      pointerEvents: "all"
    }}><Popover open={menuOpen} onOpenChange={open_0 => {
        if (!open_0) {
          setMenuOpen(false);
        }
      }} modal={false}><PopoverTrigger asChild={true}><button className="border-ds-neutral-300 text-ds-text-subtle dark:text-ds-text-disabled bg-ds-surface-card shadow-ds-soft flex h-6 w-6 items-center justify-center rounded-full border transition-all hover:h-7 hover:w-7 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 hover:shadow-md dark:border-zinc-600 dark:hover:border-blue-500 dark:hover:bg-blue-950 dark:hover:text-blue-400" onClick={e_0 => {
            e_0.stopPropagation();
            setMenuOpen(_temp5);
          }} title="Add On Complete node (runs after loop finishes)"><IconPlusOutline18 className="h-3 w-3" /></button></PopoverTrigger><PopoverContent className="border-ds-border-default bg-ds-surface-card w-64 p-2 shadow-xl" align="center" onPointerDownOutside={() => setMenuOpen(false)}><div className="micro-label mb-1.5 px-3 pt-1 font-semibold text-amber-600 dark:text-amber-400">On Complete (post-loop)</div><div className="flex flex-col"><button onClick={handleAddAction} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-green-50 dark:hover:bg-green-950/20"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-50 dark:bg-green-950/30"><IconBoltOutline18 className="h-4.5 w-4.5 text-green-600 dark:text-green-400" /></div><div className="flex-1"><div className="body-sm-semibold">Add Action</div><div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">Execute after loop finishes</div></div></button><div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-1 h-px" /><button onClick={handleAddLogic} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors duration-150 hover:bg-purple-50 dark:hover:bg-purple-950/20"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-950/30"><IconCodeBranchOutline18 className="h-4.5 w-4.5 text-purple-600 dark:text-purple-400" /></div><div className="flex-1"><div className="body-sm-semibold">Add Logic Gate</div><div className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">Control flow conditionally</div></div></button></div></PopoverContent></Popover></div>;
    $[57] = centerX;
    $[58] = disabled;
    $[59] = handleAddAction;
    $[60] = handleAddLogic;
    $[61] = menuOpen;
    $[62] = plusBtnY;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== t12 || $[65] !== t13 || $[66] !== t14 || $[67] !== t15 || $[68] !== t16 || $[69] !== t5) {
    t17 = <>{t5}{t12}{t13}{t14}{t15}{t16}</>;
    $[64] = t12;
    $[65] = t13;
    $[66] = t14;
    $[67] = t15;
    $[68] = t16;
    $[69] = t5;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  return t17;
}
function _temp5(prev_0) {
  return !prev_0;
}
function _temp4(prev) {
  return !prev;
}
function _temp3(c) {
  return c.targetY;
}
const ForEachLoopBoundary = memo(ForEachLoopBoundaryInner);
export default ForEachLoopBoundary;